import { cn } from '@/lib/utils'
import React, { createContext, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { PopoverPosition } from './utils'

interface MenuRootProps extends Omit<React.ComponentProps<'div'>, 'position'> {
    children: React.ReactNode
    id?: string
    className?: string
    position?: PopoverPosition
    arrow?: boolean
    /** Abre o menu automaticamente ao montar. */
    defaultOpen?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    /** `false` libera eventos fora do menu aberto — necessário em menus por hover. */
    modal?: boolean
}

interface MenuContextValue {
    position: PopoverPosition
    arrow?: boolean
}

const MenuContext = createContext<MenuContextValue>({ position: 'center' })

const useMenuContext = () => useContext(MenuContext)

function MenuRoot({
    children,
    id,
    className,
    position = 'center',
    arrow,
    defaultOpen,
    open: openProp,
    onOpenChange,
    modal,
    ...rest
}: MenuRootProps) {
    const [openState, setOpenState] = useState(defaultOpen ?? false)
    const isControlled = openProp !== undefined
    const open = isControlled ? openProp : openState

    const handleOpenChange = (aberto: boolean) => {
        if (!isControlled) setOpenState(aberto)
        onOpenChange?.(aberto)
    }

    return (
        <MenuContext.Provider value={{ position, arrow }}>
            <DropdownMenu
                open={open}
                onOpenChange={handleOpenChange}
                modal={modal}
            >
                <div id={id} className={className} {...rest}>
                    {children}
                </div>
            </DropdownMenu>
        </MenuContext.Provider>
    )
}

interface MenuTriggerProps {
    children: React.ReactNode
    pointer?: boolean
}

function MenuTrigger({ children, pointer }: MenuTriggerProps) {
    if (!React.isValidElement(children)) {
        throw new Error('MenuTrigger must have a valid child')
    }

    return (
        <DropdownMenuTrigger
            asChild
            className={pointer ? 'cursor-pointer' : undefined}
            onClick={e => e.stopPropagation()}
        >
            {children}
        </DropdownMenuTrigger>
    )
}

type MenuListProps = React.ComponentProps<typeof DropdownMenuContent>

function MenuList({ children, ...props }: MenuListProps) {
    const { position, arrow } = useMenuContext()

    return (
        <DropdownMenuContent
            data-testid="popup-menu"
            side={getSide(position)}
            align={getAlign(position)}
            noArrow={!arrow}
            {...props}
        >
            {children}
        </DropdownMenuContent>
    )
}

type MenuItemProps = React.ComponentProps<typeof DropdownMenuItem> & {
    selected?: boolean
}

function MenuItemRoot({
    selected,
    className,
    onClick,
    href,
    ...props
}: MenuItemProps) {
    const navigate = useNavigate()

    return (
        <DropdownMenuItem
            data-selected={selected || undefined}
            className={cn('data-[selected]:bg-accent', className)}
            onClick={e => {
                onClick?.(e)
                if (href) navigate(href)
            }}
            {...props}
        />
    )
}

function MenuItemIcon({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            className={cn(
                'flex shrink-0 items-center [&_svg]:size-5',
                className,
            )}
            {...props}
        />
    )
}

function MenuItemText({ className, ...props }: React.ComponentProps<'span'>) {
    return <span className={cn('flex-1 truncate', className)} {...props} />
}

const MenuItem = Object.assign(MenuItemRoot, {
    Icon: MenuItemIcon,
    Text: MenuItemText,
})

type MenuPopoverProps = React.ComponentProps<typeof DropdownMenuContent>

function MenuPopover({ children, ...props }: MenuPopoverProps) {
    const { position, arrow } = useMenuContext()

    return (
        <DropdownMenuContent
            side={getSide(position)}
            align={getAlign(position)}
            noArrow={!arrow}
            {...props}
        >
            {children}
        </DropdownMenuContent>
    )
}

/**
 * @example
 * import Menu from "@/global/components/Menu";
 * import { Button } from '@/global/components/Button'
 *
 *  return (
 *      <Menu.Root arrow>
 *          <Menu.Trigger>
 *              <Button>Trigger</Button>
 *          </Menu.Trigger>
 *          <Menu.List>
 *              <Menu.Item href={`/example`}>Botão do menu</Menu.Item>
 *          </Menu.List>
 *      </Menu.Root>
 *  )
 */
const Menu = {
    Root: MenuRoot,
    Trigger: MenuTrigger,
    List: MenuList,
    Item: MenuItem,
    /** Igual ao Menu.List, mas sem semântica de item — não fecha ao clicar */
    Popover: MenuPopover,
}

type MenuSide = 'top' | 'bottom'
type MenuAlign = 'start' | 'center' | 'end'

function getSide(position: PopoverPosition): MenuSide {
    return position.startsWith('top') ? 'top' : 'bottom'
}

function getAlign(position: PopoverPosition): MenuAlign {
    if (position.endsWith('left')) return 'start'
    if (position.endsWith('right')) return 'end'
    return 'center'
}

export default Menu
